/* ============================================================
   NotificationBell — CB-2 (286c2f72): Navbar bell + unread badge.
   Polls the unread count while authenticated (60s, paused in
   hidden tabs) and hosts the NotificationDrawer. Renders nothing
   for anonymous sessions.
   s9 (d2de0c1c): optional onNavigate(target) threads the drawer's
   row taps to the app-level navigate(); the drawer closes first.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { unreadBadgeText } from './notificationsView';
import type { NotificationTarget } from './notificationsView';
import { NotificationDrawer } from './NotificationDrawer';

const POLL_INTERVAL_MS = 60_000;

export function NotificationBell({ onNavigate }: { onNavigate?: (target: NotificationTarget) => void }) {
  const { auth } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(async () => {
    try {
      const list = await api.getNotifications(1, 1);
      setUnread(list.unreadCount);
    } catch {
      // Silent: a badge is best-effort; the drawer owns the error surface.
    }
  }, []);

  // Poll while authenticated; pause when the tab is hidden.
  useEffect(() => {
    if (!auth) {
      setUnread(0);
      return;
    }
    void refreshUnread();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshUnread();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [auth, refreshUnread]);

  if (!auth) return null;

  const badge = unreadBadgeText(unread);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={`Уведомления${badge ? ` (${badge} непрочитанных)` : ''}`}
        title="Уведомления"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          fontSize: 'var(--fs-h3)',
          lineHeight: 1,
          color: 'var(--color-text-secondary)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-separator)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 3C8.97 3 6.5 5.47 6.5 8.5V11.4L5 14.2C4.8 14.6 5.1 15 5.5 15H18.5C18.9 15 19.2 14.6 19 14.2L17.5 11.4V8.5C17.5 5.47 15.03 3 12 3Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
            fill="none"
          />
          <path d="M9.8 17.5C10.15 18.6 11 19.4 12 19.4C13 19.4 13.85 18.6 14.2 17.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" fill="none" />
        </svg>
        {badge && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-primary)',
              color: '#FFFFFF',
              fontSize: '11px',
              fontWeight: 'var(--fw-semibold)',
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {badge}
          </span>
        )}
      </button>
      <NotificationDrawer
        open={open}
        onClose={() => setOpen(false)}
        role={auth?.role ?? null}
        onNavigate={
          onNavigate
            ? (target) => {
                setOpen(false);
                onNavigate(target);
              }
            : undefined
        }
      />
    </>
  );
}
